import {get} from 'lodash';
import {emptyObject} from '@packages/utilities/fp/utils';
import {dissoc} from '@packages/utilities/key-map';
import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import {
  TAG_BY_NODE_TYPE,
  GET_FROM_DATA,
} from '@packages/systems/core/constants/TagToTypeMigration';
import type {JSONNode} from '@packages/shared/types/JSONNode';

const omitTag = dissoc('tag');

const setType = (type: string, jsonNode: JSONNode) => {
  const data = !TAG_BY_NODE_TYPE[type]
    ? jsonNode.data
    : TAG_BY_NODE_TYPE[type] === GET_FROM_DATA
      ? {...jsonNode.data, tag: jsonNode.tag}
      : omitTag(jsonNode.data || emptyObject);

  const json: JSONNode = {
    _id: jsonNode._id,
    type,
  };

  if (jsonNode.classes) {
    json.classes = jsonNode.classes;
  }

  if (data && data !== emptyObject) {
    json.data = data;
  }

  if (jsonNode.children) {
    json.children = jsonNode.children;
  }

  if (jsonNode.v) {
    json.v = jsonNode.v;
  }

  if (jsonNode.bind) {
    json.bind = jsonNode.bind;
  }

  if (jsonNode.dataType) {
    json.dataType = jsonNode.dataType;
  }

  if (jsonNode.meta) {
    json.meta = jsonNode.meta;
  }

  return json;
};

/**
 * This function updates a jsonNode by giving it a `type`, removing `tag` and
 * adding `data.tag` for nodes with configurable tags.
 *
 * **Warning**
 * This function is pure and cannot be used as is in the context of jsonNode
 * migrations.
 */
export const migrateJsonNodeFromTagToType = (jsonNode: JSONNode) => {
  const {type, tag, data} = jsonNode;

  // This includes migrated nodes and text nodes.
  if (!tag) {
    return jsonNode;
  }

  // If we have a type we can skip all the checks.
  if (type) {
    return setType(type, jsonNode);
  }

  const formType = get(data, ['form', 'type']);
  const commerceType = get(data, ['commerce', 'type']);

  // The symbol instance check should always be first, as there can be collisions (a symbol instance _is_ a div block)
  if (DomTypeUtils.isSymbolInst(data)) {
    return setType('Symbol', jsonNode);
  }

  if (DomTypeUtils.isBackgroundVideoWrapper(data)) {
    return setType('BackgroundVideoWrapper', jsonNode);
  }

  if (tag === 'base') {
    return setType('Base', jsonNode);
  }

  if (tag === 'body') {
    return setType('Body', jsonNode);
  }

  if (tag === 'wf-comment') {
    return setType('Comment', jsonNode);
  }

  if (tag === 'wf-custom-code') {
    return setType('CustomCode', jsonNode);
  }

  if (commerceType === 'add-to-cart-wrapper') {
    return setType('CommerceAddToCartWrapper', jsonNode);
  }

  if (commerceType === 'add-to-cart-form') {
    return setType('CommerceAddToCartForm', jsonNode);
  }

  if (commerceType === 'add-to-cart-quantity-input') {
    return setType('CommerceAddToCartQuantityInput', jsonNode);
  }

  if (commerceType === 'add-to-cart-option-list') {
    return setType('CommerceAddToCartOptionList', jsonNode);
  }

  if (commerceType === 'add-to-cart-option') {
    return setType('CommerceAddToCartOption', jsonNode);
  }

  if (commerceType === 'add-to-cart-option-label') {
    return setType('CommerceAddToCartOptionLabel', jsonNode);
  }

  if (commerceType === 'add-to-cart-option-select') {
    return setType('CommerceAddToCartOptionSelect', jsonNode);
  }

  if (commerceType === 'add-to-cart-button') {
    return setType('CommerceAddToCartButton', jsonNode);
  }

  if (commerceType === 'add-to-cart-out-of-stock') {
    return setType('CommerceAddToCartOutOfStock', jsonNode);
  }

  if (commerceType === 'add-to-cart-error') {
    return setType('CommerceAddToCartError', jsonNode);
  }

  if (commerceType === 'add-to-cart-error-text') {
    return setType('CommerceAddToCartErrorText', jsonNode);
  }

  if (commerceType === 'cart-container-wrapper') {
    return setType('CommerceCartContainerWrapper', jsonNode);
  }

  if (commerceType === 'cart-open-link') {
    return setType('CommerceCartOpenLink', jsonNode);
  }

  if (commerceType === 'cart-open-link-count') {
    return setType('CommerceCartOpenLinkCount', jsonNode);
  }

  if (commerceType === 'cart-open-link-image') {
    return setType('CommerceCartOpenLinkImage', jsonNode);
  }

  if (commerceType === 'cart-wrapper') {
    return setType('CommerceCartWrapper', jsonNode);
  }

  if (commerceType === 'cart-container') {
    return setType('CommerceCartContainer', jsonNode);
  }

  if (commerceType === 'cart-header') {
    return setType('CommerceCartHeader', jsonNode);
  }

  if (commerceType === 'cart-heading') {
    return setType('CommerceCartHeading', jsonNode);
  }

  if (commerceType === 'cart-form-wrapper') {
    return setType('CommerceCartFormWrapper', jsonNode);
  }

  if (commerceType === 'cart-form') {
    return setType('CommerceCartForm', jsonNode);
  }

  if (commerceType === 'cart-empty-state') {
    return setType('CommerceCartEmptyState', jsonNode);
  }

  if (commerceType === 'cart-error-state') {
    return setType('CommerceCartErrorState', jsonNode);
  }

  if (commerceType === 'cart-list') {
    return setType('CommerceCartList', jsonNode);
  }

  if (commerceType === 'cart-footer') {
    return setType('CommerceCartFooter', jsonNode);
  }

  if (commerceType === 'cart-line-item') {
    return setType('CommerceCartLineItem', jsonNode);
  }

  if (commerceType === 'cart-close-link') {
    return setType('CommerceCartCloseLink', jsonNode);
  }

  if (commerceType === 'cart-remove-link') {
    return setType('CommerceCartRemoveLink', jsonNode);
  }

  if (commerceType === 'cart-order-value') {
    return setType('CommerceCartOrderValue', jsonNode);
  }

  if (commerceType === 'cart-checkout-button') {
    return setType('CommerceCartCheckoutButton', jsonNode);
  }

  if (commerceType === 'cart-item') {
    return setType('CommerceCartItem', jsonNode);
  }

  if (commerceType === 'cart-item-image') {
    return setType('CommerceCartItemImage', jsonNode);
  }

  if (commerceType === 'cart-item-info') {
    return setType('CommerceCartItemInfo', jsonNode);
  }

  if (commerceType === 'cart-product-name') {
    return setType('CommerceCartProductName', jsonNode);
  }

  if (commerceType === 'cart-error-text') {
    return setType('CommerceCartErrorText', jsonNode);
  }

  if (commerceType === 'cart-quantity') {
    return setType('CommerceCartQuantity', jsonNode);
  }

  if (DomTypeUtils.isDynamoWrapper(data)) {
    return setType('DynamoWrapper', jsonNode);
  }

  if (DomTypeUtils.isDynamoList(data)) {
    return setType('DynamoList', jsonNode);
  }

  if (DomTypeUtils.isDynamoItem(data)) {
    return setType('DynamoItem', jsonNode);
  }

  if (DomTypeUtils.isDynamoEmpty(data)) {
    return setType('DynamoEmpty', jsonNode);
  }

  if (DomTypeUtils.isGridColumn(data)) {
    return setType('Column', jsonNode);
  }

  if (DomTypeUtils.isGridContainer(data)) {
    return setType('Container', jsonNode);
  }

  if (DomTypeUtils.isGridRow(data)) {
    return setType('Row', jsonNode);
  }

  if (DomTypeUtils.isGridSection(data)) {
    return setType('Section', jsonNode);
  }

  if (DomTypeUtils.isFacebookWidget(data)) {
    return setType('Facebook', jsonNode);
  }

  if (DomTypeUtils.isGPlusWidget(data)) {
    return setType('GooglePlus', jsonNode);
  }

  if (tag === 'head') {
    return setType('Head', jsonNode);
  }

  if (tag === 'html') {
    return setType('Document', jsonNode);
  }

  if (DomTypeUtils.isIconWidget(data)) {
    return setType('Icon', jsonNode);
  }

  if (DomTypeUtils.isImage(tag)) {
    return setType('Image', jsonNode);
  }

  if (tag === 'br') {
    return setType('LineBreak', jsonNode);
  }

  if (DomTypeUtils.isMapWidget(data)) {
    return setType('MapWidget', jsonNode);
  }

  if (tag === 'span') {
    return setType('Span', jsonNode);
  }

  if (tag === 'figcaption') {
    return setType('Figcaption', jsonNode);
  }

  if (tag === 'figure') {
    return setType('Figure', jsonNode);
  }

  if (tag === 'strong') {
    return setType('Strong', jsonNode);
  }

  if (tag === 'em') {
    return setType('Emphasized', jsonNode);
  }

  if (tag === 'sup') {
    return setType('Superscript', jsonNode);
  }

  if (tag === 'sub') {
    return setType('Subscript', jsonNode);
  }

  if (tag === 'code') {
    return setType('InlineCode', jsonNode);
  }

  if (tag === 'link') {
    return setType('Resource', jsonNode);
  }

  if (tag === 'script') {
    return setType('Script', jsonNode);
  }

  if (tag === 'style') {
    return setType('Style', jsonNode);
  }

  if (DomTypeUtils.isSliderWrapper(data)) {
    return setType('SliderWrapper', jsonNode);
  }

  if (DomTypeUtils.isSliderMask(data)) {
    return setType('SliderMask', jsonNode);
  }

  if (DomTypeUtils.isSliderSlide(data)) {
    return setType('SliderSlide', jsonNode);
  }

  if (DomTypeUtils.isSliderArrow(data)) {
    return setType('SliderArrow', jsonNode);
  }

  if (DomTypeUtils.isSliderNav(data)) {
    return setType('SliderNav', jsonNode);
  }

  if (tag === 'title') {
    return setType('Title', jsonNode);
  }

  if (DomTypeUtils.isTwitterWidget(data)) {
    return setType('Twitter', jsonNode);
  }

  if (DomTypeUtils.isVideo(data)) {
    return setType('Video', jsonNode);
  }

  if (DomTypeUtils.isHtmlEmbed(data)) {
    return setType('HtmlEmbed', jsonNode);
  }

  if (DomTypeUtils.isNavbarBrand(data)) {
    return setType('NavbarBrand', jsonNode);
  }

  if (DomTypeUtils.isNavbarButton(data)) {
    return setType('NavbarButton', jsonNode);
  }

  if (DomTypeUtils.isNavbarContainer(data)) {
    return setType('NavbarContainer', jsonNode);
  }

  if (DomTypeUtils.isNavbarLink(data)) {
    return setType('NavbarLink', jsonNode);
  }

  if (DomTypeUtils.isNavbarMenu(data)) {
    return setType('NavbarMenu', jsonNode);
  }

  if (DomTypeUtils.isNavbarWrapper(data)) {
    return setType('NavbarWrapper', jsonNode);
  }

  if (tag === 'meta') {
    return setType('Meta', jsonNode);
  }

  if (DomTypeUtils.isBlockquote(tag)) {
    return setType('Blockquote', jsonNode);
  }

  if (DomTypeUtils.isDropdownLink(data)) {
    return setType('DropdownLink', jsonNode);
  }

  if (DomTypeUtils.isDropdownList(data)) {
    return setType('DropdownList', jsonNode);
  }

  if (DomTypeUtils.isDropdownToggle(data)) {
    return setType('DropdownToggle', jsonNode);
  }

  if (DomTypeUtils.isDropdownWrapper(data)) {
    return setType('DropdownWrapper', jsonNode);
  }

  if (DomTypeUtils.isFormBlockLabel(data)) {
    return setType('FormBlockLabel', jsonNode);
  }

  if (formType === 'recaptcha') {
    return setType('FormReCaptcha', jsonNode);
  }

  if (DomTypeUtils.isFormButton(data)) {
    return setType('FormButton', jsonNode);
  }

  if (DomTypeUtils.isFormCheckboxInput(data)) {
    return setType('FormCheckboxInput', jsonNode);
  }

  if (DomTypeUtils.isFormCheckboxWrapper(data)) {
    return setType('FormCheckboxWrapper', jsonNode);
  }

  if (DomTypeUtils.isFormErrorMessage(data)) {
    return setType('FormErrorMessage', jsonNode);
  }

  if (DomTypeUtils.isFormForm(data)) {
    return setType('FormForm', jsonNode);
  }

  if (DomTypeUtils.isFormInlineLabel(data)) {
    return setType('FormInlineLabel', jsonNode);
  }

  if (DomTypeUtils.isFormRadioInput(data)) {
    return setType('FormRadioInput', jsonNode);
  }

  if (DomTypeUtils.isFormRadioWrapper(data)) {
    return setType('FormRadioWrapper', jsonNode);
  }

  if (DomTypeUtils.isFormSelect(data)) {
    return setType('FormSelect', jsonNode);
  }

  if (DomTypeUtils.isFormSuccessMessage(data)) {
    return setType('FormSuccessMessage', jsonNode);
  }

  if (formType === 'file-upload-input') {
    return setType('FormFileUploadInput', jsonNode);
  }

  if (formType === 'file-upload') {
    return setType('FormFileUploadWrapper', jsonNode);
  }

  if (formType === 'file-upload-default') {
    return setType('FormFileUploadDefault', jsonNode);
  }

  if (formType === 'file-upload-uploading') {
    return setType('FormFileUploadUploading', jsonNode);
  }

  if (formType === 'file-upload-uploading-btn') {
    return setType('FormFileUploadUploadingBtn', jsonNode);
  }

  if (formType === 'file-upload-success') {
    return setType('FormFileUploadSuccess', jsonNode);
  }

  if (formType === 'file-upload-file') {
    return setType('FormFileUploadFile', jsonNode);
  }

  if (formType === 'file-upload-file-name') {
    return setType('FormFileUploadFileName', jsonNode);
  }

  if (formType === 'file-upload-remove-link') {
    return setType('FormFileUploadRemoveLink', jsonNode);
  }

  if (formType === 'file-upload-error') {
    return setType('FormFileUploadError', jsonNode);
  }

  if (formType === 'file-upload-error-msg') {
    return setType('FormFileUploadErrorMsg', jsonNode);
  }

  if (formType === 'file-upload-label') {
    return setType('FormFileUploadLabel', jsonNode);
  }

  if (formType === 'file-upload-info') {
    return setType('FormFileUploadInfo', jsonNode);
  }

  if (DomTypeUtils.isFormTextarea(data)) {
    return setType('FormTextarea', jsonNode);
  }

  if (DomTypeUtils.isFormTextInput(data)) {
    return setType('FormTextInput', jsonNode);
  }

  if (DomTypeUtils.__DEPRECATED__isFormWrapper(data)) {
    return setType('FormWrapper', jsonNode);
  }

  if (DomTypeUtils.isSearchForm(data)) {
    return setType('SearchForm', jsonNode);
  }

  if (DomTypeUtils.isSearchInput(data)) {
    return setType('SearchInput', jsonNode);
  }

  if (DomTypeUtils.isSearchButton(data)) {
    return setType('SearchButton', jsonNode);
  }

  if (DomTypeUtils.isSearchResultItem(data)) {
    return setType('SearchResultItem', jsonNode);
  }

  if (DomTypeUtils.isSearchResultList(data)) {
    return setType('SearchResultList', jsonNode);
  }

  if (DomTypeUtils.isSearchResultWrapper(data)) {
    return setType('SearchResultWrapper', jsonNode);
  }

  if (DomTypeUtils.isSearchResultEmptyMessage(data)) {
    return setType('SearchResultEmpty', jsonNode);
  }

  if (DomTypeUtils.isParagraph(tag)) {
    return setType('Paragraph', jsonNode);
  }

  if (DomTypeUtils.isTabsContent(data)) {
    return setType('TabsContent', jsonNode);
  }

  if (DomTypeUtils.isTabsLink(data)) {
    return setType('TabsLink', jsonNode);
  }

  if (DomTypeUtils.isTabsMenu(data)) {
    return setType('TabsMenu', jsonNode);
  }

  if (DomTypeUtils.isTabsPane(data)) {
    return setType('TabsPane', jsonNode);
  }

  if (DomTypeUtils.isTabsWrapper(data)) {
    return setType('TabsWrapper', jsonNode);
  }

  if (DomTypeUtils.isHeading(tag)) {
    return setType('Heading', jsonNode);
  }

  if (DomTypeUtils.isLightboxWrapper(data)) {
    return setType('LightboxWrapper', jsonNode);
  }

  if (DomTypeUtils.isLink(tag)) {
    return setType('Link', jsonNode);
  }

  if (DomTypeUtils.isList(data)) {
    return setType('List', jsonNode);
  }

  if (DomTypeUtils.isListItem(data)) {
    return setType('ListItem', jsonNode);
  }

  if (DomTypeUtils.isRichBlock(tag, data)) {
    return setType('RichText', jsonNode);
  }

  if (tag === 'iframe') {
    return setType('Iframe', jsonNode);
  }

  // Fall back to `Block` for unknown nodes.
  return setType('Block', jsonNode);
};
